import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from 'src/modules/shared/components/molecules/modals/modal';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { PiInfo } from 'react-icons/pi';
import {
  TCCredential,
  TCCredentialKind,
  TCCredentialUpdateSecretBodyParams,
} from 'src/api/generated';
import {
  buildCredentialRotateSecretFormSchema,
  type CredentialRotateSecretFormValues,
} from 'src/modules/settings/validations/credentials.validation';

/**
 * Props for CredentialRotateSecretModal component.
 */
export interface CredentialRotateSecretModalProps {
  /** Credential whose secret is being rotated, or null when modal should be closed. */
  credential: TCCredential | null;
  /** Called when modal should be closed. */
  onClose: () => void;
  /** Called when form is submitted with a valid payload. */
  onSubmit: (params: {
    credentialId: string;
    body: TCCredentialUpdateSecretBodyParams;
  }) => Promise<void>;
  /** Whether the submit action is in progress. */
  loading: boolean;
}

/**
 * CredentialRotateSecretModal component.
 */
export const CredentialRotateSecretModal: React.FC<
  CredentialRotateSecretModalProps
> = ({ credential, onClose, onSubmit, loading }) => {
  const { t } = useI18n();
  const isOpen = !!credential;

  const kind = credential?.kind ?? TCCredentialKind.API_KEY;

  const schema = useMemo(
    () => buildCredentialRotateSecretFormSchema(kind),
    [kind]
  );

  const defaultValues = useMemo<CredentialRotateSecretFormValues>(
    () => ({
      apiKey: '',
      bearerToken: '',
      basicUsername: '',
      basicPassword: '',
      customSecretJson: '{}',
    }),
    []
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CredentialRotateSecretFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    if (!credential) return;
    reset(defaultValues);
  }, [credential, defaultValues, reset]);

  const handleFormSubmit = async (values: CredentialRotateSecretFormValues) => {
    if (!credential?.credentialId) return;

    let secret: TCCredentialUpdateSecretBodyParams['secret'];
    switch (credential.kind) {
      case TCCredentialKind.API_KEY:
        secret = { apiKey: values.apiKey?.trim() ?? '' };
        break;
      case TCCredentialKind.BEARER:
        secret = { token: values.bearerToken?.trim() ?? '' };
        break;
      case TCCredentialKind.BASIC:
        secret = {
          username: values.basicUsername?.trim() ?? '',
          password: values.basicPassword?.trim() ?? '',
        };
        break;
      case TCCredentialKind.CUSTOM:
        secret = JSON.parse(values.customSecretJson ?? '{}') as Record<
          string,
          unknown
        >;
        break;
      default:
        throw new Error('Unsupported credential kind');
    }

    try {
      await onSubmit({
        credentialId: credential.credentialId,
        body: { kind: credential.kind, secret },
      });
      onClose();
    } catch {
      // onSubmit is expected to notify the user.
    }
  };

  const renderSecretInputs = () => {
    switch (kind) {
      case TCCredentialKind.API_KEY:
        return (
          <LabelTextInput
            label={t('settings.credentials.form.apiKey')}
            labelPosition="top"
            inputProps={{
              ...register('apiKey'),
              error: errors.apiKey?.message,
            }}
          />
        );
      case TCCredentialKind.BEARER:
        return (
          <LabelTextInput
            label={t('settings.credentials.form.bearerToken')}
            labelPosition="top"
            inputProps={{
              ...register('bearerToken'),
              error: errors.bearerToken?.message,
            }}
          />
        );
      case TCCredentialKind.BASIC:
        return (
          <div className="space-y-3">
            <LabelTextInput
              label={t('settings.credentials.form.username')}
              labelPosition="top"
              inputProps={{
                ...register('basicUsername'),
                error: errors.basicUsername?.message,
              }}
            />
            <LabelTextInput
              label={t('settings.credentials.form.password')}
              labelPosition="top"
              inputProps={{
                ...register('basicPassword'),
                error: errors.basicPassword?.message,
              }}
            />
          </div>
        );
      case TCCredentialKind.CUSTOM:
        return (
          <LabelTextArea
            label={t('settings.credentials.form.secretJson')}
            labelPosition="top"
            inputProps={{
              ...register('customSecretJson'),
              error: errors.customSecretJson?.message,
              rows: 6,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={
        <Text size="h6" weight="semibold">
          {t('settings.credentials.rotate.title')}
        </Text>
      }
      body={
        credential ? (
          <form
            className="w-full space-y-3"
            onSubmit={handleSubmit(handleFormSubmit)}
            id="credential-rotate-form"
          >
            <Text size="small" color="secondary">
              {credential.name ?? '-'} ({credential.provider ?? '-'})
            </Text>
            {renderSecretInputs()}
            <div className="pt-1" />
            <Alert
              variant="neutral"
              appearance="soft"
              size="sm"
              icon={<PiInfo className="w-4 h-4" />}
              message={t('settings.credentials.form.encryptionInfo')}
            />
          </form>
        ) : null
      }
      footer={
        <div className="flex justify-end gap-3 px-4 py-3">
          <Button
            variant="neutral"
            appearance="ghost"
            onClick={onClose}
            disabled={loading || isSubmitting}
          >
            {t('settings.credentials.actions.cancel')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="credential-rotate-form"
            loading={loading || isSubmitting}
            disabled={loading || isSubmitting}
          >
            {t('settings.credentials.actions.rotate')}
          </Button>
        </div>
      }
      dialogClassName="max-w-lg"
    />
  );
};
